import { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, deleteDoc, updateDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { Search, Trash2, Mail, Loader2, AlertCircle, Clock, Shield, User, UserMinus, ShieldAlert, X, ShieldCheck, CheckCircle2 } from 'lucide-react';
import InstructorApprovalModal from '../../components/admin/InstructorApprovalModal';
import InstructorCard from '../../components/dashboard/InstructorCard';

// Countdown Component for each user row
const Countdown = ({ expiryDate }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (!expiryDate) return;

    const calculateTime = () => {
      const now = new Date().getTime();
      const expiry = expiryDate.toDate ? expiryDate.toDate().getTime() : new Date(expiryDate).getTime();
      const distance = expiry - now;

      if (distance < 0) {
        setTimeLeft('انتهى الوقت');
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft(`${days}ي ${hours}س ${minutes}د ${seconds}ث`);
    };

    calculateTime();
    const interval = setInterval(calculateTime, 1000);

    return () => clearInterval(interval);
  }, [expiryDate]);

  return (
    <div className="flex flex-col items-center">
      <span className="font-mono text-mdfont-bold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10 px-2 py-1 rounded-lg border border-primary-100 dark:border-primary-500/20">
        {timeLeft}
      </span>
    </div>
  );
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isProcessing, setIsProcessing] = useState(null);

  // States for Timer Modal
  const [isTimerModalOpen, setIsTimerModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [suspendDays, setSuspendDays] = useState(1);

  // States for Instructor Approval
  const [activeTab, setActiveTab] = useState('all'); // all | pending_instructors | approved_instructors
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      let usersData = querySnapshot.docs.map(docSnapshot => {
        const data = docSnapshot.data();
        let dateValue = 0;
        if (data.registrationDate) dateValue = data.registrationDate.toMillis?.() || 0;
        else if (data.createdAt) dateValue = data.createdAt.toMillis?.() || 0;
        
        return {
          id: docSnapshot.id,
          ...data,
          dateValue,
          dateStr: data.registrationDate
            ? data.registrationDate.toDate().toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })
            : data.createdAt 
              ? data.createdAt.toDate().toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })
              : 'غير معروف'
        };
      });

      // Sort heavily so newest registration is first
      usersData.sort((a, b) => b.dateValue - a.dateValue);

      setUsers(usersData);
      setFilteredUsers(usersData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching auth users:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const term = searchTerm.toLowerCase();

    let baseUsers = users;
    if (activeTab === 'pending_instructors') {
      baseUsers = users.filter(u => u.role === 'instructor' && u.instructorData?.status === 'pending');
    } else if (activeTab === 'approved_instructors') {
      baseUsers = users.filter(u => u.role === 'instructor' && u.instructorData?.status === 'approved');
    }

    const filtered = baseUsers.filter(user =>
      (user.name && user.name.toLowerCase().includes(term)) ||
      (user.email && user.email.toLowerCase().includes(term)) ||
      (user.id && user.id.toLowerCase().includes(term))
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users, activeTab]);

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف حساب هذا المستخدم نهائياً؟ سيتم حذفه تلقائياً من نظام التوثيق (Auth) وجميع سجلات وبيانات المنصة.')) return;
    setIsProcessing(id);
    try {
      await deleteDoc(doc(db, 'users', id));
    } catch (err) {
      console.error(err);
      alert('حدث خطأ أثناء الحذف');
    } finally {
      setIsProcessing(null);
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'suspended' ? 'active' : 'suspended';
    setIsProcessing(user.id);
    try {
      await updateDoc(doc(db, 'users', user.id), {
        status: newStatus,
        // If activating, clear the timer
        ...(newStatus === 'active' && { expiryDate: null })
      });
    } catch (err) {
      console.error(err);
      alert('حدث خطأ أثناء تعديل الحالة');
    } finally {
      setIsProcessing(null);
    }
  };

  const handleSetTimer = async () => {
    if (!selectedUser) return;
    setIsProcessing(selectedUser.id);
    try {
      const expiry = new Date();
      expiry.setDate(expiry.getDate() + parseInt(suspendDays));

      await updateDoc(doc(db, 'users', selectedUser.id), {
        status: 'suspended',
        expiryDate: expiry
      });
      setIsTimerModalOpen(false);
    } catch (err) {
      console.error(err);
      alert('حدث خطأ أثناء ضبط المؤقت');
    } finally {
      setIsProcessing(null);
      setSelectedUser(null);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2">إدارة الحسابات (المشتركين)</h1>
        <p className="text-slate-500 dark:text-zinc-400">التحكم في وصول الطلاب والمدربين، وتفعيل حسابات المدربين الجديدة.</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-slate-200 dark:border-white/10 pb-4">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-5 py-2.5 rounded-xl font-bold transition-all ${activeTab === 'all' ? 'bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5'}`}
        >
          كل الحسابات
        </button>
        <button
          onClick={() => setActiveTab('approved_instructors')}
          className={`px-5 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 ${activeTab === 'approved_instructors' ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5'}`}
        >
          <Shield className="w-5 h-5" />
          المدربون المعتمدون
        </button>
        <button
          onClick={() => setActiveTab('pending_instructors')}
          className={`px-5 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 ${activeTab === 'pending_instructors' ? 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5'}`}
        >
          <ShieldCheck className="w-5 h-5" />
          طلبات المدربين
          {users.filter(u => u.role === 'instructor' && u.instructorData?.status === 'pending').length > 0 && (
            <span className="bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full min-w-[20px] text-center">
              {users.filter(u => u.role === 'instructor' && u.instructorData?.status === 'pending').length}
            </span>
          )}
        </button>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-sm">
        <div className="p-6 border-b border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-zinc-900/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative w-full max-w-md">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="ابحث بالاسم، الإيميل، أو المعرف (ID)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-11 py-3 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all text-slate-900 dark:text-white font-bold"
            />
          </div>
          <div className="text-lg font-bold text-slate-500 dark:text-zinc-400">
            إجمالي الحسابات: <span className="text-primary-600 dark:text-primary-400">{filteredUsers.length}</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <Loader2 className="w-10 h-10 animate-spin text-primary-600 mb-4" />
              <p className="font-bold">جاري تحميل الحسابات...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <AlertCircle className="w-12 h-12 text-slate-300 dark:text-zinc-700 mb-4" />
              <p className="text-lg font-bold">لم يتم العثور على حسابات</p>
            </div>
          ) : activeTab === 'approved_instructors' ? (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 bg-slate-50/50 dark:bg-black/20">
              {filteredUsers.map((user) => (
                <InstructorCard key={user.id} instructor={user} />
              ))}
            </div>
          ) : (
            <table className="w-full text-right border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-zinc-900 border-b border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400 text-mdfont-bold uppercase tracking-wider">
                  <th className="px-6 py-4">المستخدم</th>
                  <th className="px-6 py-4">المعرف (ID)</th>
                  <th className="px-6 py-4">الرتبة</th>
                  <th className="px-6 py-4">الحالة</th>
                  <th className="px-6 py-4 text-center">الوقت المتبقي</th>
                  <th className="px-6 py-4">تاريخ الانضمام</th>
                  <th className="px-6 py-4 text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-primary-600 font-bold overflow-hidden border border-slate-200 dark:border-white/10 shadow-sm">
                          {user.photoURL ? <img src={user.photoURL} alt={user.name} className="w-full h-full object-cover" /> : <User className="w-6 h-6 opacity-40" />}
                        </div>
                        <div>
                          <div className="font-bold text-slate-900 dark:text-white line-clamp-1">{user.name}</div>
                          <div className="text-md text-slate-500 font-sans" dir="ltr">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-[10px] font-mono text-slate-400 bg-slate-50 dark:bg-black/30 px-2 py-1 rounded border border-slate-100 dark:border-white/5" dir="ltr">{user.id.substring(0, 10)}...</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className={`inline-flex items-center gap-1.5 text-md font-bold px-3 py-1.5 rounded-full ${user.role === 'admin' ? 'bg-indigo-50 text-indigo-600 border border-indigo-100' :
                        user.role === 'instructor' ? 'bg-amber-50 text-amber-600 border border-amber-100 dark:bg-amber-500/10 dark:border-amber-500/20' :
                          'bg-slate-100 text-slate-600 dark:bg-white/5 dark:text-zinc-400'
                        }`}>
                        {user.role === 'admin' && <Shield className="w-3.5 h-3.5" />}
                        {user.role === 'instructor' && <ShieldCheck className="w-3.5 h-3.5" />}
                        {user.role === 'admin' ? 'مدير' : user.role === 'instructor' ? 'مدرب' : 'طالب'}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-md font-extrabold ${user.status === 'suspended' ? 'bg-red-50 text-red-600 border border-red-100' :
                        user.instructorData && user.instructorData.status === 'pending' ? 'bg-orange-50 text-orange-600 border border-orange-100' :
                          'bg-green-50 text-green-600 border border-green-100'
                        }`}>
                        {user.status === 'suspended' ? 'موقوف' : user.instructorData?.status === 'pending' ? 'قيد المراجعة' : 'نشط'}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      {user.expiryDate ? <Countdown expiryDate={user.expiryDate} /> : <span className="text-slate-300 dark:text-zinc-700 text-center block">-</span>}
                    </td>
                    <td className="px-6 py-5 text-mdfont-medium text-slate-500 whitespace-nowrap">{user.dateStr}</td>
                    <td className="px-6 py-5 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {user.role === 'instructor' && user.instructorData?.status === 'pending' && (
                          <button
                            onClick={() => { setSelectedInstructor(user); setIsApprovalModalOpen(true); }}
                            className="p-2.5 text-amber-500 hover:text-white bg-amber-50 hover:bg-amber-500 dark:bg-amber-500/10 rounded-xl transition-all font-bold flex items-center gap-1 px-4"
                            title="ترقية وتفعيل المدرب"
                          >
                            <CheckCircle2 className="w-4.5 h-4.5" />
                            موافقة
                          </button>
                        )}
                        <button
                          onClick={() => { setSelectedUser(user); setIsTimerModalOpen(true); }}
                          className="p-2.5 text-slate-400 hover:text-primary-600 bg-slate-100 hover:bg-primary-50 dark:bg-white/5 dark:hover:bg-primary-500/10 rounded-xl transition-all"
                          title="جدولة إيقاف"
                        >
                          <Clock className="w-4.5 h-4.5" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(user)}
                          disabled={isProcessing === user.id}
                          className={`p-2.5 rounded-xl transition-all ${user.status === 'suspended' ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-amber-600 bg-amber-50 hover:bg-amber-100'} disabled:opacity-50`}
                          title={user.status === 'suspended' ? 'تنشيط الحساب' : 'إيقاف يدوي'}
                        >
                          {user.status === 'suspended' ? <User className="w-4.5 h-4.5" /> : <UserMinus className="w-4.5 h-4.5" />}
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          disabled={isProcessing === user.id}
                          className="p-2.5 text-slate-400 hover:text-red-600 bg-slate-100 hover:bg-red-50 dark:bg-white/5 dark:hover:bg-red-500/10 rounded-xl transition-all disabled:opacity-50"
                          title="حذف الحساب"
                        >
                          {isProcessing === user.id ? <Loader2 className="w-4.5 h-4.5 animate-spin" /> : <Trash2 className="w-4.5 h-4.5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Timer Modal */}
      {isTimerModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200" dir="rtl">
            <div className="p-6 text-center border-b border-slate-100 dark:border-white/10 relative">
              <button onClick={() => setIsTimerModalOpen(false)} className="absolute left-6 top-6 text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
              <div className="w-16 h-16 bg-primary-50 dark:bg-primary-500/10 text-primary-600 mx-auto rounded-2xl flex items-center justify-center mb-4 border border-primary-100 dark:border-primary-500/20 shadow-sm">
                <Clock className="w-8 h-8" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white font-sans">جدولة إيقاف الحساب</h2>
              <p className="text-slate-500 dark:text-zinc-400 mt-2 text-md">سيتم إيقاف حساب <span className="font-extrabold text-slate-900 dark:text-white px-1 underline decoration-primary-300 decoration-2">{selectedUser?.name}</span> تلقائياً بعد المدة المحددة.</p>
            </div>

            <div className="p-8 space-y-6">
              <div>
                <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-3 text-center">أدخل مدة التفعيل (بالأيام)</label>
                <div className="flex items-center justify-center gap-4">
                  <button onClick={() => setSuspendDays(prev => Math.max(1, prev - 1))} className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-xl font-bold text-slate-600 dark:text-white hover:bg-slate-200 transition-colors">-</button>
                  <input
                    type="number"
                    min="1"
                    value={suspendDays}
                    onChange={(e) => setSuspendDays(e.target.value)}
                    className="w-24 px-4 py-3 bg-slate-50 dark:bg-black/50 border-2 border-primary-100 dark:border-primary-500/20 rounded-2xl outline-none focus:border-primary-500 text-center text-2xl font-black text-primary-600 dark:text-primary-400"
                  />
                  <button onClick={() => setSuspendDays(prev => parseInt(prev) + 1)} className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-xl font-bold text-slate-600 dark:text-white hover:bg-slate-200 transition-colors">+</button>
                </div>
              </div>
              <div className="pt-2 flex flex-col gap-3">
                <button
                  onClick={handleSetTimer}
                  disabled={isProcessing === selectedUser?.id}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-4 rounded-2xl font-bold transition-all shadow-lg shadow-primary-500/20 hover:shadow-primary-500/40 flex justify-center items-center gap-2"
                >
                  {isProcessing === selectedUser?.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <span>تأكيد وجدولة الإيقاف</span>}
                </button>
                <button
                  onClick={() => setIsTimerModalOpen(false)}
                  className="w-full bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:text-white py-3 rounded-2xl font-bold transition-all text-slate-600"
                >
                  إلغاء العملية
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instructor Approval Modal */}
      {isApprovalModalOpen && (
        <InstructorApprovalModal
          instructor={selectedInstructor}
          onClose={() => {
            setIsApprovalModalOpen(false);
            setSelectedInstructor(null);
          }}
        />
      )}
    </div>
  );
}
